"use client";

import { useEffect, useState } from "react";
import { io, type Socket } from "socket.io-client";
import { useTranslation } from "react-i18next";
import { formatShares } from "@/lib/format";

interface Trade {
  id: string;
  priceCents: number;
  sizeShares: string;
  outcomeId: string;
  outcomeSortOrder: number;
  platformFeeCents?: string;
  createdAt: string;
}

interface IncomingTradeEvent {
  tradeId: string;
  marketSlug: string;
  priceCents: number;
  sizeShares: string;
  outcomeId: string;
  outcomeSortOrder: number;
  platformFeeCents: string;
  createdAt: string;
}

interface Props {
  slug: string;
  initialTrades: Trade[];
}

const MAX_VISIBLE = 25;

/**
 * STORY-308 phase 2 — render server-fetched trades and merge live
 * `market:trade` events from the WS feed in place. New trades animate
 * in at the top with a brief flash so the user sees the update.
 *
 * Phase 2 still does NOT cover: socket auth, reconnect-resume / event
 * replay across disconnects, multi-event types beyond `market:trade`.
 */
export function LiveTradesList({ slug, initialTrades }: Props) {
  const { t } = useTranslation();
  const [trades, setTrades] = useState<Trade[]>(initialTrades);
  const [connected, setConnected] = useState(false);
  const [flashIds, setFlashIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const apiBase = process.env.NEXT_PUBLIC_API_URL;
    if (!apiBase) return;
    const url = apiBase.replace(/\/$/, "");

    const socket: Socket = io(url, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 5,
    });

    socket.on("connect", () => {
      setConnected(true);
      socket.emit("subscribe", { slug });
    });
    socket.on("disconnect", () => setConnected(false));
    socket.on("market:trade", (event: IncomingTradeEvent) => {
      if (event.marketSlug !== slug) return;
      const incoming: Trade = {
        id: event.tradeId,
        priceCents: event.priceCents,
        sizeShares: event.sizeShares,
        outcomeId: event.outcomeId,
        outcomeSortOrder: event.outcomeSortOrder,
        platformFeeCents: event.platformFeeCents,
        createdAt: event.createdAt,
      };
      setTrades((prev) => {
        if (prev.some((t) => t.id === incoming.id)) return prev;
        return [incoming, ...prev].slice(0, MAX_VISIBLE);
      });
      setFlashIds((prev) => new Set(prev).add(incoming.id));
      // Clear the flash after a short delay; CSS handles fade-out via the class
      setTimeout(() => {
        setFlashIds((prev) => {
          const next = new Set(prev);
          next.delete(incoming.id);
          return next;
        });
      }, 1500);
    });

    return () => {
      socket.emit("unsubscribe", { slug });
      socket.disconnect();
    };
  }, [slug]);

  if (trades.length === 0) {
    return (
      <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-center text-[11px] text-[var(--color-text3)]">
        {t("liveTrades.emptyState")}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[10px] border border-[var(--color-border)] bg-[var(--color-surface)]">
      <div className="flex items-center justify-between border-b border-[var(--color-border)] px-3 py-2 text-[9px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
        <div className="grid flex-1 grid-cols-[1fr_1fr_1fr_auto] gap-3">
          <span>{t("liveTrades.side")}</span>
          <span>{t("liveTrades.price")}</span>
          <span>{t("liveTrades.size")}</span>
          <span className="text-right">{t("liveTrades.when")}</span>
        </div>
        {connected && (
          <span className="ml-3 inline-flex items-center gap-1 normal-case tracking-normal text-[var(--color-text3)]">
            <span
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ background: "var(--color-yes)" }}
            />
            <span className="text-[10px]">{t("liveTrades.liveIndicator")}</span>
          </span>
        )}
      </div>
      <ul className="divide-y divide-[var(--color-border)]">
        {trades.map((trade) => {
          const isYes = trade.outcomeSortOrder === 0;
          const color = isYes ? "var(--color-yes)" : "var(--color-no)";
          const isFlashing = flashIds.has(trade.id);
          return (
            <li
              key={trade.id}
              className="grid grid-cols-[1fr_1fr_1fr_auto] gap-3 px-3 py-1.5 font-mono text-[10.5px] tabular-nums transition-colors duration-1000"
              style={{
                background: isFlashing
                  ? "color-mix(in srgb, var(--color-yes) 10%, transparent)"
                  : undefined,
              }}
            >
              <span className="font-bold" style={{ color }}>
                {isYes ? t("liveTrades.yesUpper") : t("liveTrades.noUpper")}
              </span>
              <span className="text-[var(--color-text)]">
                {trade.priceCents}¢
              </span>
              <span className="text-[var(--color-text2)]">
                {/* Migration 0145 — sizeShares is centi-shares (1 share = 100). */}
                {formatShares(trade.sizeShares)}
              </span>
              <span className="text-right text-[var(--color-text3)]">
                {formatRelativeTime(trade.createdAt)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const d = Math.floor(hr / 24);
  return `${d}d`;
}
